import { useState, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Upload, FileText, ImageIcon, Sparkles, ArrowRight, AlertCircle, CheckCircle, Loader2 } from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';
import { Button } from '@/components/ui/button';
import Cookies from 'js-cookie';
import { getApiUrl } from '@/config/api';
import { MAX_UPLOAD_SIZE_MB } from '@/config/upload';

const SeasonalMenuUpload = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [error, setError] = useState('');
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [processingId, setProcessingId] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [duplicateMenuFound, setDuplicateMenuFound] = useState<any>(null);
  const [isCopyingMenu, setIsCopyingMenu] = useState(false);

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    
    const droppedFile = e.dataTransfer.files[0];
    validateAndSetFile(droppedFile);
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      validateAndSetFile(selectedFile);
    }
  };

  const validateAndSetFile = (file: File) => {
    setError('');
    
    // Check file type - accept PDF and common image formats
    const allowedTypes = [
      'application/pdf',
      'image/png', 
      'image/jpeg',
      'image/jpg'
    ];
    
    if (!allowedTypes.includes(file.type)) {
      setError('Please upload a PDF file or image (PNG, JPEG)');
      return;
    }
    
    // Check file size (100MB limit to match backend)
    const maxSize = MAX_UPLOAD_SIZE_MB * 1024 * 1024;
    if (file.size > maxSize) {
      setError(`File size must be less than ${MAX_UPLOAD_SIZE_MB}MB`);
      return;
    }
    
    setFile(file);
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleNext = async () => {
    if (!file || !id) {
      setError('Please upload your menu before continuing');
      return;
    }
    
    setError('');
    
    try {
      // Create FormData for file upload
      const formData = new FormData();
      formData.append('file', file);
      formData.append('seasonal_menu_id', id);
      
      // Get current user's auth token from cookies
      const token = Cookies.get('auth_token');
      if (!token) {
        toast({
          title: "Authentication Required",
          description: "Please log in to upload menu",
          variant: "destructive"
        });
        navigate('/restaurant-login');
        return;
      }
      
      // Upload to regular menu endpoint with seasonal_menu_id
      const response = await fetch(getApiUrl('menus/upload'), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || error.message || 'Failed to upload menu');
      }
      
      const data = await response.json();
      console.log('Upload response:', data);

      // Check if this is a duplicate menu offer
      if (data.action === 'duplicate_menu_found' && data.offer_copy) {
        // Show duplicate menu dialog
        setDuplicateMenuFound(data);
        setUploadSuccess(false);
        return;
      }

      // Store processing ID and show success state
      setProcessingId(data.processing_id);
      sessionStorage.setItem('seasonal_processing_id', data.processing_id);
      sessionStorage.setItem('seasonal_menu_id', id);
      setUploadSuccess(true);
    } catch (error) {
      console.error('Upload error:', error);
      setError(error instanceof Error ? error.message : 'Failed to upload menu');
    }
  };

  const handleProcessMenu = async () => {
    if (!processingId || !id) {
      setError('No processing ID available');
      return;
    }
    
    setIsProcessing(true);
    setError('');
    
    try {
      const token = Cookies.get('auth_token');
      if (!token) {
        console.error('No auth token for processing');
        setError('Authentication required');
        setIsProcessing(false);
        return;
      }
      
      const response = await fetch(getApiUrl(`menus/process/${processingId}`), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          seasonal_menu_id: parseInt(id)
        })
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to start processing');
      }
      
      // Store the seasonal menu ID and navigate to processing page
      sessionStorage.setItem('seasonal_menu_id', id);
      sessionStorage.setItem('seasonal_processing_id', processingId);
      navigate('/seasonal-menu-processing');
    } catch (error) {
      console.error('Processing error:', error);
      setError(error instanceof Error ? error.message : 'Failed to start processing');
      setIsProcessing(false);
    }
  };

  const handleBack = () => {
    navigate(`/seasonal-menu/${id}/edit`);
  };
  
  const handleUploadAnother = () => {
    setFile(null);
    setUploadSuccess(false);
    setProcessingId(null);
    setError('');
    setDuplicateMenuFound(null);
  };

  const handleAcceptDuplicateMenu = async () => {
    if (!duplicateMenuFound || !id) return;

    setIsCopyingMenu(true);
    setError('');

    try {
      const token = Cookies.get('auth_token');
      if (!token) {
        setError('Authentication required');
        return;
      }

      // Call the copy menu endpoint
      const response = await fetch(getApiUrl('menus/copy-menu'), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          source_rest_id: duplicateMenuFound.source_menu.rest_id,
          source_location_id: duplicateMenuFound.source_menu.location_id,
          seasonal_menu_id: parseInt(id)
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to copy menu');
      }

      const result = await response.json();

      toast({
        title: "✅ Menu copied successfully!",
        description: `${result.items_copied} items have been copied to your seasonal menu.`
      });

      // Navigate to seasonal menu review page
      navigate(`/seasonal-menu/${id}/review`);
    } catch (error) {
      console.error('Error copying menu:', error);
      setError(error instanceof Error ? error.message : 'Failed to copy menu');
    } finally {
      setIsCopyingMenu(false);
    }
  };

  const handleRejectDuplicateMenu = () => {
    // Clear the duplicate menu state and allow user to upload a different menu
    setDuplicateMenuFound(null);
    setFile(null);
    setUploadSuccess(false);
    setError('');
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return bytes + ' bytes';
    else if (bytes < 1048576) return Math.round(bytes / 1024) + ' KB';
    else return Math.round(bytes / 1048576) + ' MB';
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#1f4ed1] to-[#2256e5]">
      {/* Header */}
      <div className="border-b border-white/10 backdrop-blur-sm bg-white/5">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={handleBack}
              className="flex items-center gap-2 text-white/80 hover:text-white transition-colors"
            >
              <ArrowLeft className="h-5 w-5" />
              Back to Menu Options
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white/10 backdrop-blur-md rounded-xl border border-white/20 p-8"
        >
          {/* Header */}
          <div className="text-center mb-8">
            <div className="inline-block bg-yellow-400/20 px-3 py-1.5 rounded-full mb-4">
              <span className="text-yellow-400 font-medium text-xs flex items-center gap-1.5">
                <Sparkles className="w-3 h-3" />
                {uploadSuccess ? 'READY TO PROCESS' : 'SEASONAL MENU UPLOAD'}
              </span>
            </div>
            
            <h1 className="text-2xl md:text-3xl font-bold text-white mb-3">
              {uploadSuccess ? (
                <>
                  Process Your{" "}
                  <span className="bg-gradient-to-r from-yellow-400 via-yellow-300 to-yellow-400 bg-clip-text text-transparent">
                    Seasonal Menu
                  </span>
                </>
              ) : (
                <>
                  Upload Your{" "}
                  <span className="bg-gradient-to-r from-yellow-400 via-yellow-300 to-yellow-400 bg-clip-text text-transparent">
                    Seasonal Menu
                  </span>
                </>
              )}
            </h1>
            <p className="text-white/80 text-base">
              {uploadSuccess 
                ? 'Your seasonal menu is uploaded and ready for AI processing'
                : 'Share your seasonal menu PDF and let our AI extract all the items'
              }
            </p>
          </div>

          {/* Upload Area - only show if not uploaded successfully */}
          {!uploadSuccess && (
            <div className="mb-6">
              <div
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={handleUploadClick}
                className={`
                  relative border-2 border-dashed rounded-xl p-12 text-center cursor-pointer
                  transition-all duration-200
                  ${isDragging 
                    ? 'border-yellow-400 bg-yellow-400/10' 
                    : 'border-white/30 hover:border-white/50 bg-white/5 hover:bg-white/10'
                  }
                `}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf,.png,.jpg,.jpeg"
                  onChange={handleFileSelect}
                  className="hidden"
                />
                
                {!file ? (
                  <>
                    <Upload className="w-16 h-16 text-yellow-400 mx-auto mb-4" />
                    <h3 className="text-xl font-semibold text-white mb-2">
                      {isDragging ? 'Drop your menu here' : 'Drop your seasonal menu here'}
                    </h3>
                    <p className="text-white/60 mb-4">
                      PDF or image files • Click to browse from your computer
                    </p>
                    <p className="text-white/40 text-sm">
                      PDF, PNG, JPEG files • Maximum {MAX_UPLOAD_SIZE_MB}MB
                    </p>
                  </>
                ) : (
                  <>
                    {file.type.startsWith('image/') ? (
                      <ImageIcon className="w-16 h-16 text-yellow-400 mx-auto mb-4" />
                    ) : (
                      <FileText className="w-16 h-16 text-yellow-400 mx-auto mb-4" />
                    )}
                    <h3 className="text-xl font-semibold text-white mb-2">
                      {file.name}
                    </h3>
                    <p className="text-white/60 mb-4">
                      {formatFileSize(file.size)}
                    </p>
                    <Button
                      className="bg-white/10 backdrop-blur-sm border border-white/20 text-white hover:bg-white/20 transition-all"
                      onClick={(e) => {
                        e.stopPropagation();
                        setFile(null);
                        setError('');
                      }}
                    >
                      Remove File
                    </Button>
                  </>
                )}
              </div>
              
              {/* Error Message */}
              {error && (
                <motion.div 
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mt-4 p-4 rounded-xl border bg-red-500/10 border-red-500/30 text-red-400"
                >
                  <div className="flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                    <p className="text-sm font-medium leading-relaxed">
                      {error}
                    </p>
                  </div>
                </motion.div>
              )}
            </div>
          )}

          {/* Action Buttons */}
          {!uploadSuccess ? (
            <div className="flex justify-between">
              <Button 
                onClick={handleBack}
                className="bg-white/10 backdrop-blur-sm border border-white/20 text-white hover:bg-white/20 py-3 px-6 text-base font-medium rounded-full flex items-center gap-2 transition-all"
              >
                <ArrowLeft className="w-5 h-5" />
                Back
              </Button>
              
              <Button 
                onClick={handleNext}
                disabled={!file}
                className="bg-yellow-400 text-black hover:bg-yellow-300 py-3 px-8 text-base font-bold rounded-full shadow-lg transform hover:scale-105 transition-all flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
              >
                Upload Menu
                <ArrowRight className="w-5 h-5" />
              </Button>
            </div>
          ) : (
            <div className="text-center">
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="bg-green-500/10 border border-green-500/30 rounded-xl p-6 mb-6"
              >
                <CheckCircle className="w-12 h-12 text-green-400 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-white mb-2">
                  Upload Successful!
                </h3>
                <p className="text-white/80 text-sm mb-4">
                  Your seasonal menu has been uploaded and is ready for AI processing.
                </p>
              </motion.div>
              
              <div className="flex gap-4 justify-center">
                <Button 
                  onClick={handleUploadAnother}
                  className="bg-white/10 backdrop-blur-sm border border-white/20 text-white hover:bg-white/20 py-3 px-6 text-base font-medium rounded-full"
                >
                  Upload Different Menu
                </Button>
                
                <Button 
                  onClick={handleProcessMenu}
                  disabled={isProcessing}
                  className="bg-yellow-400 text-black hover:bg-yellow-300 py-3 px-8 text-base font-bold rounded-full shadow-lg transform hover:scale-105 transition-all flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                >
                  {isProcessing ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Starting...
                    </>
                  ) : (
                    <>
                      Process Menu
                      <ArrowRight className="w-5 h-5" />
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}
        </motion.div>

        {/* Help text */}
        <p className="text-center text-white/60 text-sm mt-6">
          Your seasonal menu will be analyzed by AI to extract dishes, prices, and descriptions
        </p>
      </div>

      {/* Duplicate Menu Modal */}
      {duplicateMenuFound && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-2xl max-w-lg w-full p-6 shadow-xl"
          >
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-dineai-yellow/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Sparkles className="w-8 h-8 text-dineai-yellow" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                Seasonal Menu Already Processed!
              </h2>
              <p className="text-gray-600 text-lg leading-relaxed">
                {duplicateMenuFound.ai_message}
              </p>
            </div>

            {/* Menu Details */}
            <div className="bg-gray-50 rounded-lg p-4 mb-6">
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-500">Location:</span>
                  <span className="font-medium text-gray-900">
                    {duplicateMenuFound.source_menu.location_name}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Items Found:</span>
                  <span className="font-medium text-gray-900">
                    {duplicateMenuFound.source_menu.total_items} menu items
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Processed:</span>
                  <span className="font-medium text-gray-900">
                    {duplicateMenuFound.source_menu.days_ago === 0 ? 'Today' :
                     duplicateMenuFound.source_menu.days_ago === 1 ? 'Yesterday' :
                     `${duplicateMenuFound.source_menu.days_ago} days ago`}
                  </span>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="space-y-3">
              <Button
                onClick={handleAcceptDuplicateMenu}
                disabled={isCopyingMenu}
                className="w-full bg-dineai-blue text-white hover:bg-dineai-blue/90 py-3 rounded-lg font-semibold transition-colors"
              >
                {isCopyingMenu ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin mr-2" />
                    Copying menu...
                  </>
                ) : (
                  'Yes, use this menu for this seasonal menu'
                )}
              </Button>
              <Button
                onClick={handleRejectDuplicateMenu}
                disabled={isCopyingMenu}
                variant="outline"
                className="w-full border-gray-300 text-gray-700 hover:bg-gray-50 py-3 rounded-lg font-semibold"
              >
                No, I want to upload a different menu
              </Button>
            </div>

            {error && (
              <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
                <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-800">{error}</p>
              </div>
            )}
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default SeasonalMenuUpload;